import { useCallback } from 'react';

import { createActionsHook, createHook, createStore } from 'react-sweet-state';

import { fg } from '@atlaskit/platform-feature-flags';
import type {
	BrowserContextPayloadData,
	ChatContextPayload,
	ChatContextState,
	DashboardInsightsActionsPayloadData,
	EditorContextPayloadData,
	WhiteboardContextPayloadData,
	WorkflowContextPayloadData,
} from '@atlaskit/rovo-triggers';
import type { ReadContentCommandResult } from '@atlassian/rovo-content-bridge-api-commands';

import { useUserPreferences } from '../../user-preferences';

export type Store = {
	editorContext: EditorContextPayloadData;
	whiteboardContext?: WhiteboardContextPayloadData;
	browserContext?: BrowserContextPayloadData;
	dashboardContext?: DashboardInsightsActionsPayloadData;
	jiraWorkflowContext?: WorkflowContextPayloadData;
	// All the new context will be added under this key
	aiFeatureContext: ChatContextState;
	/**
	 * For AI-first creation, we need to store the content targets in the store.
	 * This is used to determine which content target to use for content mutations.
	 */
	contentTargets: Partial<Record<ContentContext, ContentTarget>>;
};

type ContentType = 'page' | 'whiteboard';

/**
 * A piece of content which Rovo chat is able to communicate with directly in order
 * to mutate it after it has been created. Considerations:
 *
 * - Rovo <-> content can communicate via inline experience inside of the content (e.g. the inline toolbar)
 * - Rovo <-> content[] is a possibility - consider 'layering' of the staging area in AI-first creation.
 * - Rovo <-> content[] is a future possibility with more and more content mutations being possible via Rovo.
 */
type ContentTarget = {
	/**
	 * The id of the content target, which will be supplied to backend APIs,
	 * and yielded in every chunk of network stream events.
	 */
	id: string;
	/**
	 * The type of the content target, which will be used to determine which context
	 * to use for create and edit mutations.
	 */
	type: 'page' | 'whiteboard';
	/**
	 * Whether the content target can provide context for create and edit mutations.
	 */
	canProvideContext: boolean;
};

/**
 * Content is rendered in a Rovo context. If it is 'real' content outside of the staging area, it is rendered
 * in the `global` Rovo context (with Rovo side-chat being a global experience). If it is rendered in the staging
 * area modal, then it is rendered to the left, with chat embedded in the same UI experience.
 */
export type ContentContext = 'staging-area' | 'global';

const store = createStore({
	initialState: {
		editorContext: undefined,
		whiteboardContext: undefined,
		browserContext: undefined,
		dashboardContext: undefined,
		jiraWorkflowContext: undefined,
		aiFeatureContext: {},
		contentTargets: {},
	} as Store,
	actions: {
		getAllContext:
			() =>
			({ getState }) =>
				getState(),
		getAIFeatureContext:
			() =>
			({ getState }) => {
				const currentState = getState().aiFeatureContext;
				// Delete all properties that are undefined
				Object.keys(currentState).forEach(
					(key) => currentState[key] === undefined && delete currentState[key],
				);
				// Avoid sending an empty object
				return Object.keys(currentState).length ? currentState : undefined;
			},
		setAIFeatureContext:
			(chatContextData: ChatContextPayload) =>
			({ getState, setState }) => {
				const currentAIFeatureContextState = getState().aiFeatureContext;
				const { contextKey, setContext } = chatContextData;
				if (contextKey) {
					const contextUpdated = setContext(currentAIFeatureContextState[contextKey]);

					setState({
						aiFeatureContext: {
							...currentAIFeatureContextState,
							[contextKey]: contextUpdated,
						},
					});
				}
			},
		getDashboardContext:
			() =>
			({ getState }) =>
				getState().dashboardContext,
		setDashboardContext:
			(dashboardContext: DashboardInsightsActionsPayloadData) =>
			({ setState }) => {
				setState({
					dashboardContext,
				});
			},
		getJiraWorkflowContext:
			() =>
			({ getState }) =>
				getState().jiraWorkflowContext,
		setJiraWorkflowContext:
			(jiraWorkflowContext: WorkflowContextPayloadData) =>
			({ setState }) => {
				setState({
					jiraWorkflowContext,
				});
			},
		getEditorContext:
			() =>
			({ getState }) =>
				getState().editorContext,
		setEditorContext:
			(editorContext: EditorContextPayloadData) =>
			({ setState }) => {
				setState({
					editorContext,
				});
			},
		getWhiteboardContext:
			() =>
			({ getState }) =>
				getState().whiteboardContext,
		setWhiteboardContext:
			(whiteboardContext: WhiteboardContextPayloadData) =>
			({ setState }) => {
				setState({
					whiteboardContext,
				});
			},
		getBrowserContext:
			() =>
			({ getState }) =>
				getState().browserContext,
		setBrowserContext:
			(browserContext: BrowserContextPayloadData) =>
			({ setState }) => {
				setState({
					browserContext,
				});
			},
		getTarget:
			(contentContext: ContentContext) =>
			({ getState }) =>
				getState().contentTargets[contentContext],
		hasTarget:
			(contentContext: ContentContext) =>
			({ getState }) =>
				!!getState().contentTargets[contentContext],
		setTarget:
			(toAdd: ContentContext, target: ContentTarget) =>
			({ getState, setState }) => {
				setState({
					contentTargets: { ...getState().contentTargets, [toAdd]: target },
				});
			},
		clearTarget:
			(toRemove: ContentContext) =>
			({ getState, setState }) => {
				const currentState = getState().contentTargets;
				const nextState: Partial<Record<ContentContext, ContentTarget>> = {};

				for (const key of Object.keys(currentState)) {
					const k = key as ContentContext;
					if (k === toRemove) {
						continue;
					}

					nextState[k] = currentState[k];
				}

				setState({ contentTargets: nextState });
			},
	},
});

export const useChatContextStoreActions = createActionsHook(store);
export const useChatContextEditorContext = createHook(store, {
	selector: (state) => state.editorContext,
});
export const useChatContextWhiteboardContext = createHook(store, {
	selector: (state) => state.whiteboardContext,
});

// For testing
export { store };

export const useChatContextProvider = ({
	getContentBody,
	stagingAreaEnabled,
	useCurrentNonEditingPageAsContext,
}: {
	getContentBody: (type: ContentType, id: string) => Promise<ReadContentCommandResult | undefined>;
	stagingAreaEnabled: boolean;
	useCurrentNonEditingPageAsContext?: boolean;
}) => {
	const { preferences } = useUserPreferences();
	const sendEditorContext = preferences.useEditorContext;

	const actions = useChatContextStoreActions();
	const getTarget = actions.getTarget;
	const contentContext: ContentContext = stagingAreaEnabled ? 'staging-area' : 'global';

	const fetchContent = useCallback(
		async (type: ContentType) => {
			const contentTarget = getTarget(contentContext);
			if (!contentTarget) {
				return undefined;
			}

			const contentBody: ReadContentCommandResult | undefined = await getContentBody(
				type,
				contentTarget.id,
			);

			if (!contentBody || contentBody.contentType !== type) {
				return undefined;
			}

			switch (contentBody.contentType) {
				case 'page': {
					if (!contentBody.pageBody) {
						return undefined;
					}

					return {
						contentId: contentBody.contentId,
						document: {
							type: 'text/adf' as const,
							content: contentBody.pageBody,
						},
						selection: {
							type: 'text/markdown' as const,
							content: '',
						},
						selectionFragment: contentBody.selectionFragment,
						selectionLocalIds: contentBody.selectionLocalIds,
					};
				}
				case 'whiteboard': {
					if (!contentBody.svg) {
						return undefined;
					}

					return {
						contentId: contentBody.contentId,
						type: 'image/svg+xml' as const,
						content: contentBody.svg,
					};
				}
				default:
					return undefined;
			}
		},
		[getContentBody, getTarget, contentContext],
	);

	const getEditorContext = useCallback(
		async ({ isFirstPrompt }: { isFirstPrompt: boolean; source: string | undefined }) => {
			let context: EditorContextPayloadData | undefined;

			// try AIFC flow first
			if (fg('aifc_create_enabled')) {
				if (!isFirstPrompt || contentContext === 'global') {
					const result = (await fetchContent('page')) as EditorContextPayloadData;
					context = result;
				}
			}

			// then fallback to original flow
			if (!context && sendEditorContext) {
				context = actions.getEditorContext();
			}

			return context;
		},
		[actions, sendEditorContext, fetchContent, contentContext],
	);

	const getWhiteboardContext = useCallback(
		async ({ isFirstPrompt }: { isFirstPrompt: boolean; source: string | undefined }) => {
			let context: WhiteboardContextPayloadData | undefined;

			// try AIFC flow first
			if (fg('aifc_create_enabled')) {
				if (!isFirstPrompt || contentContext === 'global') {
					const result = (await fetchContent('whiteboard')) as WhiteboardContextPayloadData;
					context = result;
				}
			}

			// then fallback to original flow
			if (!context && sendEditorContext) {
				context = actions.getWhiteboardContext();
			}

			return context;
		},
		[actions, sendEditorContext, fetchContent, contentContext],
	);

	const getIsStagingArea = useCallback(() => {
		return stagingAreaEnabled;
	}, [stagingAreaEnabled]);

	const useCurrentBrowsingContext = useCallback(() => {
		if (useCurrentNonEditingPageAsContext === undefined) {
			return undefined;
		}
		return !stagingAreaEnabled && sendEditorContext && useCurrentNonEditingPageAsContext;
	}, [stagingAreaEnabled, sendEditorContext, useCurrentNonEditingPageAsContext]);

	return {
		...actions,
		getEditorContext,
		getWhiteboardContext,
		getIsStagingArea,
		useCurrentBrowsingContext,
	};
};
